import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { assetBackgroundStyle } from '../lib/media';

export default function PageHero({
  eyebrow,
  title,
  description,
  image,
  primaryAction,
  secondaryAction,
  stats,
  className = '',
}) {
  const [activeStat, setActiveStat] = useState(0);

  useEffect(() => {
    if (!stats?.length) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setActiveStat((current) => (current + 1) % stats.length);
    }, 4200);

    return () => window.clearInterval(interval);
  }, [stats]);

  const currentStat = stats?.length ? stats[activeStat] : null;

  return (
    <section className={`relative overflow-hidden border-b border-brand-gray-200 bg-brand-black text-white ${className}`}>
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          ...assetBackgroundStyle(image, 1800),
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(17,17,17,0.92)_0%,rgba(17,17,17,0.78)_58%,rgba(17,17,17,0.55)_100%)]" aria-hidden="true" />

      <div className="relative mx-auto grid min-h-[72vh] max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8 lg:py-24">
        <div className="max-w-3xl">
          {eyebrow ? (
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.28em] text-brand-orange">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="max-w-2xl font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl">
            {title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-8 text-brand-gray-200 sm:text-lg lg:text-xl">
            {description}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            {primaryAction ? (
              <Link
                to={primaryAction.to}
                className="inline-flex items-center justify-center bg-brand-orange px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-orange-hover"
              >
                {primaryAction.label}
                <ArrowRight className="ml-3 h-4 w-4" />
              </Link>
            ) : null}
            {secondaryAction ? (
              secondaryAction.href ? (
                <a
                  href={secondaryAction.href}
                  className="inline-flex items-center justify-center border border-white/30 px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  {secondaryAction.label}
                </a>
              ) : (
                <Link
                  to={secondaryAction.to}
                  className="inline-flex items-center justify-center border border-white/30 px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  {secondaryAction.label}
                </Link>
              )
            ) : null}
          </div>
        </div>

        {currentStat ? (
          <div className="min-h-[11rem] overflow-hidden border border-white/10 bg-white/10 backdrop-blur-[2px]">
            <div className="flex h-full transition-transform duration-700 ease-out" style={{ transform: `translateX(-${activeStat * 100}%)` }}>
              {stats.map((stat, index) => (
                <div key={stat.label} className="w-full shrink-0 p-5 sm:p-6">
                  <div className="border border-white/10 bg-black/20 p-5">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-gray-200">{stat.label}</p>
                    <p className="mt-3 text-sm leading-7 text-white/90">{stat.value}</p>
                    <div className="mt-4 flex items-center gap-2">
                      {stats.map((item, dotIndex) => (
                        <button
                          key={item.label}
                          type="button"
                          onClick={() => setActiveStat(dotIndex)}
                          className={`h-2.5 w-2.5 transition-colors ${dotIndex === index ? 'bg-brand-orange' : 'bg-white/30'}`}
                          aria-label={`Vis ${item.label}`}
                          aria-pressed={dotIndex === index}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
